import * as api from "./api";
import { getClassroomScreenshotsApi } from "../utils/getBackendUrl";
import { useUserSessionStore } from "stores/v2/userSessionStore";
import { BAGGAGE_STUDENT_USERNAME_KEY } from "js/telemetry/constants";
import { telemetryHeaders } from "./util";

interface ScreenshotsRequestPayload {
	users: string[];
	traces?: TraceContext[];
}

export type ScreenshotRequest = {
	username: string;
	trace?: Record<string, string>;
};

type Headers = {
	Authorization: string | undefined;
	"Content-Type": string;
	traceparent?: string | undefined;
	tracestate?: string | undefined;
	baggage?: string | undefined;
};

type TraceContext = {
	traceparent?: string;
	tracestate?: string;
	baggage?: string;
};

/**
 * Builds headers to be sent to classroom screenshots API.
 */
function headers(): Headers {
	return {
		Authorization: useUserSessionStore.getState().getToken(),
		"Content-Type": "application/json",
		...telemetryHeaders(),
	};
}

/**
 * Adds trace headers into `headers`. Extracts the trace data from `student`.
 */
function addTraceHeaders(headers: Headers, student: ScreenshotRequest): void {
	const studentBaggage = `${BAGGAGE_STUDENT_USERNAME_KEY}=${student.username}`;
	if (headers.baggage) {
		headers.baggage += `,${studentBaggage}`;
	} else {
		headers.baggage = studentBaggage;
	}

	// check if we have a trace context propagated from a screenshot update event from event-service-subscriber
	if (!student.trace) {
		return;
	}

	if (student.trace.traceparent) {
		headers.traceparent = student.trace.traceparent;
	}
	if (student.trace.tracestate) {
		headers.tracestate = student.trace.tracestate;
	}

	// Currently disabled as we don't want to overwite the baggage we've already set.
	// The baggage in student.trace.baggage is baggage in the screenshot update event from event-service-subscriber.
	// In the future we may merge them using baggage functions in the js opentelemetry packages.
	// There's nothing important in ESS baggage, so this is fine for now.
	// if (student.trace.baggage) {
	// 	headers.baggage = student.trace.baggage;
	// }
}

/**
 * getStudentScreenshots function fetches the screenshots for students present in the active classroom.
 * This function is being called every SCREENSHOTS_INTERVAL seconds or upon receiving the "SCREENSHOT_UPDATE" event whichever is earliest.
 * This is high traffic endpoint and any changes here need to be tested in the classroom-api-automation e2e tests
 * https://github.com/B-Communications/classroom-api-automation
 */
export async function getStudentScreenshots(students: ScreenshotRequest[], applianceId: string, timeout = 0): Promise<string | null> {
	const controller = new AbortController();
	const payload: ScreenshotsRequestPayload = {
		users: students.map((student) => student.username),
	};

	const _headers = headers();
	if (students.length === 1) {
		addTraceHeaders(_headers, students[0]);
	}

	const options = {
		method: "POST",
		headers: _headers,
		body: JSON.stringify(payload),
		signal: null as AbortSignal | null,
	};

	if (timeout) {
		setTimeout(() => controller.abort(), timeout);
		options["signal"] = controller.signal;
	}

	try {
		const data = (await api.fetch(
			`${getClassroomScreenshotsApi(window.location.hostname)}/v1/appliances/${applianceId}/screenshots`,
			options,
			false
		)) as string;
		return data;
	} catch (error) {
		console.error("Error getting student screenshots", error);
	}
	return null;
}
